import { PersonModelInterface } from "../../../models/personModel";
import { URLSearchParams } from "url";
import { useMemo } from "react";
import dayjs from "dayjs";

export const useAnalyticsData = (
    people: PersonModelInterface[],
    prevPeople: PersonModelInterface[],
    searchParams: URLSearchParams,
    paymentsPeople: PersonModelInterface[],
    prevPaymentPeople: PersonModelInterface[],
    closedCredits: PersonModelInterface[],
    prevClosedCredits: PersonModelInterface[]
) => {
    return useMemo(() => {
        const totalTurnover = people.reduce((sum, p) => sum + p.creditDetail.total, 0);
        const netIncome = people.reduce((sum, p) => sum + p.creditDetail.netProfit, 0);
        const contractCount = people.length;

        const monthlyContractsMap = new Map();
        const turnoverMap = new Map();
        const incomeMap = new Map();
        const productPreferencesMap = new Map();

        people.forEach(person => {
            let dateKey = '';

            if (searchParams.get('rangeType') === 'year') {
                dateKey = dayjs(person.createdAt).format('MM/YY');
            } else if (searchParams.get('rangeType') === 'month') {
                dateKey = `Н${dayjs(person.createdAt).week() - dayjs(person.createdAt).startOf('month').week() + 1}`;
            } else if (searchParams.get('rangeType') === 'week') {
                dateKey = dayjs(person.createdAt).format('DD/MM');
            } else if (searchParams.get('rangeType') === 'custom') {
                dateKey = dayjs(person.createdAt).format('MM/YY');
            }

            monthlyContractsMap.set(dateKey, (monthlyContractsMap.get(dateKey) || 0) + 1);
            turnoverMap.set(dateKey, (turnoverMap.get(dateKey) || 0) + person.creditDetail.total);
            incomeMap.set(dateKey, (incomeMap.get(dateKey) || 0) + (person.creditDetail.total * (person.creditDetail.percent / 100)));

            productPreferencesMap.set(
                person.phone.phoneModel,
                (productPreferencesMap.get(person.phone.phoneModel) || 0) + 1
            );
        });

        const previousTurnover = prevPeople.reduce((sum, p) => sum + p.creditDetail.total, 0);
        const previousNetIncome = prevPeople.reduce((sum, p) => sum + p.creditDetail.netProfit, 0);
        const previousContractsCount = prevPeople.length;

        const rangeType = searchParams.get('rangeType');
        let categoriesArray: string[] = [];

        if (rangeType === 'year') {
            categoriesArray = Array.from({ length: 12 }, (_, i) => dayjs().month(i).format('MM/YY'));
        } else if (rangeType === 'month') {
            categoriesArray = Array.from({ length: 5 }, (_, i) => `Н${i + 1}`);
        } else if (rangeType === 'week') {
            const startDate = dayjs(searchParams.get('start')).startOf('isoWeek');
            categoriesArray = Array.from({ length: 7 }, (_, i) => startDate.add(i, 'day').format('DD/MM'));
        } else if (rangeType === 'custom') {
            const startDate = dayjs(searchParams.get('start')).startOf('month');
            const endDate = dayjs(searchParams.get('end')).endOf('month');
            const monthsCount = endDate.diff(startDate, 'month') + 1;
            categoriesArray = Array.from({ length: monthsCount }, (_, i) =>
                startDate.add(i, 'month').format('MM/YY')
            );
        }

        categoriesArray.forEach(date => {
            monthlyContractsMap.set(date, monthlyContractsMap.get(date) ?? 0);
            turnoverMap.set(date, turnoverMap.get(date) ?? 0);
            incomeMap.set(date, incomeMap.get(date) ?? 0);
        });

        const salesTrend = {
            months: categoriesArray,
            turnover: categoriesArray.map(date => turnoverMap.get(date) ?? 0),
            income: categoriesArray.map(date => incomeMap.get(date) ?? 0)
        };

        const monthlyContracts = categoriesArray.map(date => monthlyContractsMap.get(date) ?? 0);
        const totalClientsArray = monthlyContracts.map(val => (val > 0 ? val : 0));

        const productPreferences = Array.from(productPreferencesMap.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name, count], index) => ({ rank: index + 1, name, count }));

        const percentTurnoverChange = previousTurnover
            ? ((totalTurnover - previousTurnover) / previousTurnover) * 100
            : 0;

        const percentIncomeChange = previousNetIncome
            ? ((netIncome - previousNetIncome) / previousNetIncome) * 100
            : 0;

        const percentContractsChange = previousContractsCount
            ? ((contractCount - previousContractsCount) / previousContractsCount) * 100
            : 0;

        // Include 'returned' status for payments (as closed credits)
        const totalPayments = paymentsPeople.reduce((sum, person) => {
            const personPaymentsSum =
                person.payments?.filter((p) => p.type !== "discount").reduce((acc, payment) => acc + payment.amount, 0) ?? 0;
            return sum + personPaymentsSum;
        }, 0);

        const previousTotalPayments = prevPaymentPeople.reduce((sum, p) => {
            return sum + (Array.isArray(p.payments)
                ? p.payments.filter((pay) => pay.type !== "discount").reduce((acc, payment) => acc + payment.amount, 0)
                : 0);
        }, 0);

        const percentPaymentsChange = previousTotalPayments
            ? ((totalPayments - previousTotalPayments) / previousTotalPayments) * 100
            : totalPayments > 0 ? 100 : 0;

        // Include 'returned' status for closed credits
        const closedContractsCount = closedCredits.length;
        const previousClosedContractsCount = prevClosedCredits.length;

        const percentClosedContractsChange = previousClosedContractsCount
            ? ((closedContractsCount - previousClosedContractsCount) / previousClosedContractsCount) * 100
            : closedContractsCount > 0 ? 100 : 0;

        // Exclude phone cost price for 'returned' status
        const totalCostPrice = people.reduce((sum, p) => {
            const costPrice = p.status === 'returned' ? 0 : (p.phone?.costPrice || 0);
            return sum + costPrice;
        }, 0);

        const previousTotalCostPrice = prevPeople.reduce((sum, p) => {
            const costPrice = p.status === 'returned' ? 0 : (p.phone?.costPrice || 0);
            return sum + costPrice;
        }, 0);

        const percentCostPriceChange = previousTotalCostPrice
            ? ((totalCostPrice - previousTotalCostPrice) / previousTotalCostPrice) * 100
            : totalCostPrice > 0 ? 100 : 0;

        // Net profit for closed credits (include 'returned')
        const closedCreditsNetProfit = closedCredits.reduce((sum, p) => sum + (p.creditDetail.netProfit || 0), 0);
        const prevClosedCreditsNetProfit = prevClosedCredits.reduce((sum, p) => sum + (p.creditDetail.netProfit || 0), 0);

        const percentClosedNetProfitChange = prevClosedCreditsNetProfit
            ? ((closedCreditsNetProfit - prevClosedCreditsNetProfit) / prevClosedCreditsNetProfit) * 100
            : closedCreditsNetProfit > 0 ? 100 : 0;

        return {
            totalTurnover,
            netIncome,
            contractCount,
            monthlyContracts,
            salesTrend,
            productPreferences,
            totalClientsArray,
            monthsArray: categoriesArray,
            percentTurnoverChange,
            percentIncomeChange,
            percentContractsChange,
            totalPayments,
            percentPaymentsChange,
            closedContractsCount,
            percentClosedContractsChange,
            totalCostPrice,
            percentCostPriceChange,
            closedCreditsNetProfit,
            percentClosedNetProfitChange
        };
    }, [people, prevPeople, searchParams, paymentsPeople, prevPaymentPeople, closedCredits, prevClosedCredits]);
};